import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccommodationStockState } from "../objects/accommodationStockState";
import { EMPTY_ACCOMMODATION_LIST_TOTAL, EMPTY_ACCOMMODATION_STOCK_LIST_CRITERIA, EMPTY_DISPLAYED_DATE } from "../constants/accommodationStockConstants";
import { AccommodationStock, AccommodationStockListCriteria, AccommodationStockTotal } from "../objects/accommodationStockList";
import { addDays, format } from "date-fns";
import { DisplayedDate } from "../objects/accommodationStockTable";
import { findFurthestEndDate, findNearestStartDate } from "../utils/dates/searchDates";
import { AccommodationStockUpdateCriteria } from "../objects/accommodationStockUpdate";
import { updateClientStockValue, updateRoomStockValue } from "../utils/accommodationStockUpdateStock";
import { calculAccommodationStockSubTotal, getAccommodationTotal } from "../utils/accommodationStockGetTotal";

const initialState: AccommodationStockState = {
    list_criteria: EMPTY_ACCOMMODATION_STOCK_LIST_CRITERIA,
    list_loading: false,
    accommodation_list: [],
    accommodation_list_total: EMPTY_ACCOMMODATION_LIST_TOTAL,
    displayed_dates: EMPTY_DISPLAYED_DATE,
    room_stock_criteria: null,
    client_stock_criteria: null,
    original_stocks: {}
};

const updateAccommodationStockListCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockListCriteria>): AccommodationStockState => ({
    ...state, list_criteria: action.payload
});
const updateAccommodationStockListReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStock[]>): AccommodationStockState => ({
    ...state, accommodation_list: calculAccommodationStockSubTotal(action.payload), accommodation_list_total: getAccommodationTotal(action.payload)
});


const updateAccommodationListTotalReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockTotal>): AccommodationStockState => ({
    ...state, accommodation_list_total: action.payload
});
const updateAccommodationListRoomStockReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria>): AccommodationStockState => ({
    ...state, accommodation_list: calculAccommodationStockSubTotal(updateRoomStockValue(action.payload, state.accommodation_list)), accommodation_list_total: getAccommodationTotal(updateRoomStockValue(action.payload, state.accommodation_list))
});
const updateAccommodationListClientStockReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria>): AccommodationStockState => ({
    ...state, accommodation_list: calculAccommodationStockSubTotal(updateClientStockValue(action.payload, state.accommodation_list)), accommodation_list_total: getAccommodationTotal(updateClientStockValue(action.payload, state.accommodation_list))
});

const updateAccommodationStockListLoadingReducer = (state: AccommodationStockState, action: PayloadAction<boolean>): AccommodationStockState => ({
    ...state, list_loading: action.payload
});
const incraseDisplayedDatesReducer = (state: AccommodationStockState): AccommodationStockState => {
    let new_start_date = addDays(new Date(state.displayed_dates.end_date), 1);
    let new_end_date = addDays(new_start_date, 6);

    // Ensure new_end_date does not exceed event_site_date.end_date
    const max_end_date = state.list_criteria.event_site_date?.event_end_date ? new Date(state.list_criteria.event_site_date?.event_end_date) : null;
    if (max_end_date && (new_end_date > max_end_date)) {
        new_end_date = max_end_date;
        new_start_date = addDays(max_end_date, -6);
    }

    return {
        ...state,
        displayed_dates: {
            start_date: format(new_start_date, 'yyyy-MM-dd'),
            end_date: format(new_end_date, 'yyyy-MM-dd'),
        }
    };
};
const decraseDisplayedDatesReducer = (state: AccommodationStockState): AccommodationStockState => {
    let new_end_date = addDays(new Date(state.displayed_dates.start_date), -1);
    let new_start_date = addDays(new_end_date, -6);

    // Ensure new_start_date does not go below event_site_date.start_date
    const min_start_date = state.list_criteria.event_site_date?.event_start_date ? new Date(state.list_criteria.event_site_date.event_start_date) : null;
    if (min_start_date && (new_start_date < min_start_date)) {
        new_start_date = min_start_date;
        new_end_date = addDays(min_start_date, 6);
    }

    return {
        ...state,
        displayed_dates: {
            start_date: format(new_start_date, 'yyyy-MM-dd'),
            end_date: format(new_end_date, 'yyyy-MM-dd'),
        }
    };
};

const goOnDisplayedDatesReducer = (state: AccommodationStockState, action: PayloadAction<Date>): AccommodationStockState => {
    const end_date = (state.list_criteria?.event_site_date?.event_end_date) ? findFurthestEndDate(action.payload, new Date(state.list_criteria?.event_site_date?.event_end_date)) : null;
    const start_date = (end_date && state.list_criteria?.event_site_date?.event_start_date) ? findNearestStartDate(end_date, new Date(state.list_criteria?.event_site_date?.event_start_date)) : null;
    return {
        ...state,
        displayed_dates: {
            start_date: start_date?.toISOString() || new Date().toISOString(),
            end_date: end_date?.toISOString() || addDays(new Date(), 5).toISOString()
        }
    };
};

const setDisplayedDatesReducer = (state: AccommodationStockState, action: PayloadAction<DisplayedDate>): AccommodationStockState => ({
    ...state, displayed_dates: {
        start_date: format(new Date(action.payload.start_date), 'yyyy-MM-dd'),
        end_date: format(new Date(action.payload.end_date), 'yyyy-MM-dd'),
    }
});
const updateRoomStockCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria | null>): AccommodationStockState => ({
    ...state, room_stock_criteria: action.payload
});
const updateClientStockCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria | null>): AccommodationStockState => ({
    ...state, client_stock_criteria: action.payload
});

const setOriginalStocksReducer = (state: AccommodationStockState, action: PayloadAction<Record<number, number[]>>): AccommodationStockState => ({
    ...state, original_stocks: action.payload
});

const updateOneOriginalStockReducer = (state: AccommodationStockState, action: PayloadAction<{index: number, value: number[]}>): AccommodationStockState => ({
    ...state, original_stocks: {...state.original_stocks, [action.payload.index]: action.payload.value}
});




export const accommodationStock = createSlice({
    name: 'accommodation_stock',
    initialState,
    reducers: {
        updateAccommodationStockListCriteria: updateAccommodationStockListCriteriaReducer,
        updateAccommodationStockList: updateAccommodationStockListReducer,
        updateAccommodationStockListLoading: updateAccommodationStockListLoadingReducer,
        incraseDisplayedDates: incraseDisplayedDatesReducer,
        decraseDisplayedDates: decraseDisplayedDatesReducer,
        setDisplayedDates: setDisplayedDatesReducer,
        goOnDisplayedDates: goOnDisplayedDatesReducer,
        updateRoomStockCriteria: updateRoomStockCriteriaReducer,
        updateClientStockCriteria: updateClientStockCriteriaReducer,
        updateAccommodationListTotal: updateAccommodationListTotalReducer,
        updateAccommodationListRoomStock: updateAccommodationListRoomStockReducer,
        updateAccommodationListClientStock: updateAccommodationListClientStockReducer,
        setOriginalStocks: setOriginalStocksReducer,
        updateOneOriginalStock: updateOneOriginalStockReducer,
    }
});

export const {
    updateAccommodationStockListCriteria,
    updateAccommodationStockList,
    updateAccommodationStockListLoading,
    incraseDisplayedDates,
    decraseDisplayedDates,
    setDisplayedDates,
    goOnDisplayedDates,
    updateRoomStockCriteria,
    updateClientStockCriteria,
    updateAccommodationListTotal,
    updateAccommodationListRoomStock,
    updateAccommodationListClientStock,
    setOriginalStocks,
    updateOneOriginalStock
} = accommodationStock.actions;